import { create } from 'zustand';
import { useHistory, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import isEqual from 'hs-lodash/isEqual';
const FILTER_PARAMS = ['search', 'teams', 'users', 'categories', 'tools', 'certificationStatuses'];
const PAGINATION_PARAMS = ['page'];
const SORT_PARAMS = ['sortBy', 'sortOrder'];
const TABLE_STATE_PARAMS = [...FILTER_PARAMS, ...PAGINATION_PARAMS, ...SORT_PARAMS];
const createUrlParams = (state, defaultState, currentSearch) => {
  const searchParams = new URLSearchParams(currentSearch);

  // First remove any existing filter params
  TABLE_STATE_PARAMS.forEach(param => searchParams.delete(param));

  // Then add current filter values
  TABLE_STATE_PARAMS.forEach(paramName => {
    const value = state[paramName];
    const defaultValue = defaultState[paramName];
    // only add params that are not the same as the default value
    if (value !== undefined && !isEqual(value, defaultValue)) {
      const valueString = Array.isArray(value) ? value.join(',') : value.toString();
      searchParams.set(paramName, valueString);
    }
  });
  const search = searchParams.toString();
  return search ? `?${search}` : '';
};
const parseCsvParam = param => {
  if (!param) return [];
  return param.split(',').filter(Boolean);
};
const hasStateChanged = (prevState, newState) => {
  return TABLE_STATE_PARAMS.some(key => {
    const prev = prevState[key] || [];
    const next = newState[key] || [];
    return !isEqual(prev, next);
  });
};
const genericTableDefaultState = {
  search: '',
  teams: [],
  users: [],
  categories: [],
  tools: [],
  certificationStatuses: [],
  page: 1,
  sortBy: 'NAME',
  sortOrder: 'ASC',
  isUserPinned: undefined
};
const getTableStateSetter = set => stateValuesToSet => set(store => Object.assign({}, store, {
  state: Object.assign({}, store.state, stateValuesToSet)
}));
const getTableStateCreator = tableSpecificDefaults => set => {
  const defaultState = Object.assign({}, genericTableDefaultState, tableSpecificDefaults);
  const setState = getTableStateSetter(set);
  return {
    state: defaultState,
    defaultState,
    actions: {
      setSearch: search => setState({
        search,
        page: 1
      }),
      setTeams: teams => setState({
        teams,
        page: 1
      }),
      setUsers: users => setState({
        users,
        page: 1
      }),
      setCategories: categories => setState({
        categories,
        page: 1
      }),
      setTools: tools => setState({
        tools,
        page: 1
      }),
      setCertificationStatuses: certificationStatuses => setState({
        certificationStatuses,
        page: 1
      }),
      setPage: page => setState({
        page
      }),
      setSortBy: sortBy => setState({
        sortBy,
        page: 1
      }),
      setSortOrder: sortOrder => setState({
        sortOrder,
        page: 1
      }),
      setIsUserPinned: isUserPinned => setState({
        isUserPinned
      }),
      initializeFromUrl: search => {
        const searchParams = new URLSearchParams(search || window.location.search);
        const params = Object.fromEntries(searchParams.entries());
        setState({
          search: params.search || defaultState.search,
          teams: parseCsvParam(params.teams).map(Number),
          users: parseCsvParam(params.users).map(Number),
          categories: parseCsvParam(params.categories),
          tools: parseCsvParam(params.tools),
          certificationStatuses: parseCsvParam(params.certificationStatuses),
          page: Number(params.page) || defaultState.page,
          sortBy: params.sortBy || defaultState.sortBy,
          sortOrder: params.sortOrder || defaultState.sortOrder,
          isUserPinned: params.isUserPinned === 'false' ? false : defaultState.isUserPinned
        });
      },
      resetTableState: () => setState(defaultState)
    }
  };
};

// Hook to sync store state with URL two-way (from URL on mount and from store on change)
export const useSyncTableStateWithUrl = tableStore => {
  const history = useHistory();
  const location = useLocation();
  const isInitialMount = useRef(true);
  const prevStateRef = useRef(tableStore.defaultState);

  // Initialise the store from URL on mount only
  useEffect(() => {
    if (isInitialMount.current) {
      tableStore.actions.initializeFromUrl(location.search);
      isInitialMount.current = false;
    }
  }, [tableStore.actions, location.search]);

  // Update URL when store changes, but only if the change didn't come from URL so we prevent an update loop
  useEffect(() => {
    if (!isInitialMount.current && hasStateChanged(prevStateRef.current, tableStore.state)) {
      const newSearch = createUrlParams(tableStore.state, tableStore.defaultState, location.search);
      history.replace(Object.assign({}, location, {
        search: newSearch
      }));
    }
    prevStateRef.current = tableStore.state;
  }, [history, location, tableStore.state, tableStore.defaultState]);
};
const certificationProfileActiveTableDefaultState = {
  sortBy: 'EXPIRES_AT',
  sortOrder: 'ASC'
};
const certificationProfileExpiredTableDefaultState = {
  sortBy: 'EXPIRES_AT',
  sortOrder: 'DESC'
};
const certificationProfileInProgressTableDefaultState = {
  sortBy: 'PROGRESS',
  sortOrder: 'DESC'
};
const certificationsIndexTableDefaultState = {
  sortBy: 'ACTIVE',
  sortOrder: 'DESC'
};
const teamMembersTableDefaultState = {
  sortBy: 'ACTIVE',
  sortOrder: 'DESC',
  isUserPinned: true
};
const certificationsByTeamActiveTableDefaultState = {
  sortBy: 'EXPIRES_AT',
  sortOrder: 'ASC'
};
const certificationsByTeamInProgressTableDefaultState = {
  sortBy: 'PROGRESS',
  sortOrder: 'DESC'
};
const certificationsByTeamExpiredTableDefaultState = {
  sortBy: 'EXPIRES_AT',
  sortOrder: 'DESC'
};
export const useCertificationProfileActiveTableStore = create(getTableStateCreator(certificationProfileActiveTableDefaultState));
export const useCertificationProfileExpiredTableStore = create(getTableStateCreator(certificationProfileExpiredTableDefaultState));
export const useCertificationProfileInProgressTableStore = create(getTableStateCreator(certificationProfileInProgressTableDefaultState));
export const useCertificationsTableStore = create(getTableStateCreator(certificationsIndexTableDefaultState));
export const useTeamMembersTableStore = create(getTableStateCreator(teamMembersTableDefaultState));
export const useCertificationsByTeamActiveTableStore = create(getTableStateCreator(certificationsByTeamActiveTableDefaultState));
export const useCertificationsByTeamInProgressTableStore = create(getTableStateCreator(certificationsByTeamInProgressTableDefaultState));
export const useCertificationsByTeamExpiredTableStore = create(getTableStateCreator(certificationsByTeamExpiredTableDefaultState));